import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth } from "../../config/utilities/useAuth";
import { APIGetMasterAdminInstitutions } from "../../config/utilities/APIs/masterAdmin/masterAdminApi";

const STORAGE_KEY = "masterAdminSelectedInstitutionId";

export const MasterAdminContext = createContext(null);

export const MasterAdminProvider = ({ children }) => {
  const { token } = useAuth();
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedInstitutionId, setSelectedInstitutionIdState] = useState(
    () => sessionStorage.getItem(STORAGE_KEY) || ""
  );
  const [formView, setFormViewState] = useState(null);

  const fetchInstitutions = useCallback(async () => {
    if (!token) return;

    try {
      const response = await APIGetMasterAdminInstitutions(setLoading, token);
      if (response?.status === 200 && Array.isArray(response.data?.data)) {
        setInstitutions(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching master admin institutions:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const setSelectedInstitutionId = useCallback((id) => {
    setSelectedInstitutionIdState(id);
    if (id) {
      sessionStorage.setItem(STORAGE_KEY, id);
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    fetchInstitutions();
  }, [fetchInstitutions]);

  useEffect(() => {
    if (institutions.length === 0) return;

    if (!selectedInstitutionId) {
      setSelectedInstitutionId(institutions[0].institutionId);
      return;
    }

    const exists = institutions.some(
      (inst) => inst.institutionId === selectedInstitutionId
    );
    if (!exists) {
      setSelectedInstitutionId(institutions[0].institutionId);
    }
  }, [institutions, selectedInstitutionId, setSelectedInstitutionId]);

  const selectedInstitution = useMemo(
    () =>
      institutions.find((inst) => inst.institutionId === selectedInstitutionId) ??
      null,
    [institutions, selectedInstitutionId]
  );

  const institutionOptions = useMemo(
    () =>
      institutions.map((inst) => ({
        label: inst.institutionName,
        value: inst.institutionId,
      })),
    [institutions]
  );

  const setFormView = useCallback((view) => {
    setFormViewState((prev) => {
      if (
        prev &&
        view &&
        prev.title === view.title &&
        prev.subtitle === view.subtitle &&
        prev.backLabel === view.backLabel
      ) {
        return prev;
      }
      return view;
    });
  }, []);

  const clearFormView = useCallback(() => {
    setFormViewState((prev) => (prev === null ? prev : null));
  }, []);

  const value = useMemo(
    () => ({
      institutions,
      institutionOptions,
      loading,
      selectedInstitutionId,
      selectedInstitution,
      setSelectedInstitutionId,
      fetchInstitutions,
      formView,
      setFormView,
      clearFormView,
    }),
    [
      institutions,
      institutionOptions,
      loading,
      selectedInstitutionId,
      selectedInstitution,
      setSelectedInstitutionId,
      fetchInstitutions,
      formView,
      setFormView,
      clearFormView,
    ]
  );

  return (
    <MasterAdminContext.Provider value={value}>
      {children}
    </MasterAdminContext.Provider>
  );
};

export const useMasterAdmin = () => {
  const context = useContext(MasterAdminContext);
  if (!context) {
    throw new Error("useMasterAdmin must be used within MasterAdminProvider");
  }
  return context;
};
